import { useEffect, useRef } from 'react';
import { type positionType } from './Timeline';
import Card from '@tempcover/bolt-ui/card';

export type TimelineItem = {
  date: string;
  title: string;
  content: string;
};

interface TimelineEntriesProps {
  entries: TimelineItem[];
  anchor: 'left' | 'right';
  cb: (arg: positionType) => void;
  width: '50%' | '100%';
  limit: number;
  padTop?: string;
}

const TimelineEntries = ({ entries, anchor, cb, width, limit, padTop = '0' }: TimelineEntriesProps) => {
  // Only render up to limit
  const _entries: TimelineItem[] = limit === 0 ? entries : entries.filter((_, idx) => idx < limit);

  const parentRef = useRef<HTMLDivElement | null>(null);
  const firstRef = useRef<HTMLDivElement | null>(null);
  const lastRef = useRef<HTMLDivElement | null>(null);

  const calculatePositions = () => {
    // Calculate positions of first and last timeline markers relative to their parent
    if (!parentRef.current || !firstRef.current || !lastRef.current) return null;
    const parent = Math.round(parentRef.current.getBoundingClientRect().top);
    const top = Math.round(
      (firstRef.current.getBoundingClientRect().top + firstRef.current.getBoundingClientRect().bottom) / 2 - parent
    );
    const bottom = Math.round(
      (lastRef.current.getBoundingClientRect().top + lastRef.current.getBoundingClientRect().bottom - top) / 2 - parent
    );
    return [top, bottom];
  };

  useEffect(() => {
    const positions = calculatePositions();
    // Pass positions back to parent component (via 'cb' prop) for drawing timeline.
    if (positions) cb(positions);
  }, [limit]);

  return (
    <div ref={parentRef} className={`gap-regular flex flex-col`} style={{ paddingTop: padTop, width: width }}>
      {_entries.map((entry, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === _entries.length - 1;
        return (
          <div
            key={idx}
            className={`animate-fadein relative opacity-0 ${anchor === 'right' ? 'pr-[5%]' : 'pl-[5%]'}`}
            style={{ animationDelay: idx * 100 + 'ms' }}
          >
            {/* Timeline marker */}
            <div
              ref={isFirst ? firstRef : isLast ? lastRef : null}
              aria-hidden="true"
              className={[
                `bg-primary animate-zoomout outline-primary border-neutral absolute top-0 bottom-0 m-auto h-5 w-5 rounded-[50%] border-2 outline-solid`,
                `${anchor === 'right' ? 'right-[-10px]' : 'left-[-10px]'}`,
              ].join(' ')}
            ></div>
            {/* Timeline entry */}
            <div
              className={anchor === 'right' ? 'animate-slideinleft' : 'animate-slideinright'}
              style={{ animationDelay: idx * 100 + 'ms' }}
            >
              <Card className={`relative`}>
                <div role="time" className="text-dark-200 absolute top-6 right-6">
                  <strong>{entry.date}</strong>
                </div>
                <div className="mb-2 min-h-[1em] max-w-[85%]">
                  <strong>{entry.title}</strong>
                </div>
                <div>{entry.content}</div>
              </Card>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TimelineEntries;
